/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { useAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'
import { MdClose } from 'react-icons/md'

import { Button } from '@/components/ui/Button'
import { GenericDialog, GenericDialogProps } from '@/components/ui/GenericDialog'
import { Switch } from '@/components/ui/Switch'
import { Link } from '@/i18n/navigation'
import { analyticalConsentAtom } from '@/store/cookies'

/** A description of the available properties for this component. */
type CookieDialogProps = Pick<GenericDialogProps, 'open' | 'handleClose'>

/**
 * The dialog for managing cookie preferences.
 */
const CookieDialog: React.FC<CookieDialogProps> = ({ open, handleClose }) => {
  const t = useTranslations('CookieDialog')
  const [savedAnalyticalConsent, setSavedAnalyticalConsent] = useAtom(analyticalConsentAtom)
  const [tempAnalyticalConsent, setTempAnalyticalConsent] = useState(savedAnalyticalConsent ?? false)

  /**
   * Update the local cookie consent state when the user changes their preferences.
   */
  useEffect(() => {
    setTempAnalyticalConsent(savedAnalyticalConsent ?? false)
  }, [savedAnalyticalConsent])

  /**
   * Update the cookie consent when the user changes their preferences. This also updates the Google Analytics consent.
   */
  const updateCookieConsent = async () => {
    if (tempAnalyticalConsent !== savedAnalyticalConsent) {
      await setSavedAnalyticalConsent(tempAnalyticalConsent)
    }

    handleClose()
  }

  /**
   * Handle the user's choice on the cookie consent dialog.
   *
   * @param decision - the user's choice on overall cookie consent (reject or accept all cookies)
   */
  const handleCookieConsentChoice = async (decision: boolean) => {
    await setSavedAnalyticalConsent(decision)
    handleClose()
  }

  return (
    <GenericDialog open={open} handleClose={handleClose} className={'gap-2'} showClose={false}>
      <div className={'flex flex-col gap-2'}>
        <div className={'flex flex-row justify-between items-center border-b border-graphite-100'}>
          <h2 className={'text-xl text-graphite-500 font-bold mb-2'}>{t('manageCookiePreferencesHeader')}</h2>
          <MdClose className={'text-graphite-500 cursor-pointer'} onClick={handleClose} />
        </div>
        <p className={'text-graphite-500 text-sm'}>{t('whyWeUseCookies')}</p>
        <p className={'text-graphite-500 text-sm'}>{t('necessaryCookiesDescription')}</p>
        <p className={'text-graphite-500 text-sm'}>{t('thirdPartyCookiesDescription')}</p>
        <p className={'text-graphite-500 text-sm'}>{t('yourChoicesExplained')}</p>
      </div>
      <div className={'flex flex-col gap-4 my-4'}>
        <Switch
          disabled
          enabled={true}
          label={'Essential Cookies'}
          description={t('essentialCookiesUsedForDescription')}
        />
        <Switch
          enabled={tempAnalyticalConsent}
          onChange={(enabled) => setTempAnalyticalConsent(enabled)}
          label={'Analytical Cookies'}
          description={t('analyticalCookiesUsedForDescription')}
        />
      </div>
      <p className={'text-graphite-500 text-sm'}>
        {t.rich('preferencesReminder', {
          link: (chunks) => (
            <Link href={'/privacy-policy'} className={'text-blue-500 hover:underline'} onClick={handleClose}>
              {chunks}
            </Link>
          ),
        })}
      </p>
      <div className={'flex flex-row gap-2'}>
        <Button variant={'grey'} onClick={() => handleCookieConsentChoice(false)}>
          {t('rejectAllButtonText')}
        </Button>
        <Button variant={'grey'} onClick={updateCookieConsent}>
          {t('savePreferencesButtonText')}
        </Button>
        <Button variant={'secondary'} onClick={() => handleCookieConsentChoice(true)}>
          {t('acceptAllButtonText')}
        </Button>
      </div>
    </GenericDialog>
  )
}

export { CookieDialog }
